<script lang="ts">
  import type { AppController } from '../../app/controller.svelte';
  import Toggle from '../components/Toggle.svelte';
  import Icon from '../components/Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  const outs = $derived(ctl.outputsVersion >= 0 ? ctl.outputs.list() : []);
  const screens = $derived(ctl.outputsVersion >= 0 ? ctl.outputs.screens : []);
  const mode = $derived(ctl.outputsVersion >= 0 ? ctl.outputs.mode : 'mirror');
  const wm = $derived(ctl.outputs.windowManagement);
</script>

<section class="sec" data-tour="outputs">
  <h2>Output windows</h2>
  <p class="hint first">
    Open one window per projector or screen, move it there and press <kbd>F</kbd> (or click it) to go
    fullscreen. Keep these controls on your laptop.
  </p>
  <div class="row wrap">
    <button class="primary" data-testid="open-output" onclick={() => ctl.openOutput(null)}
      >Open output window</button
    >
    <button onclick={() => ctl.outputs.identify()} disabled={outs.length === 0}>Identify</button>
    <button onclick={() => ctl.outputs.closeAll()} disabled={outs.length === 0}>Close all</button>
  </div>
</section>

<section class="sec" data-tour="preview-section">
  <h2>Preview before live</h2>
  <Toggle
    label={`Preview mode${ctl.keyHint('preview.toggle')}`}
    checked={ctl.previewMode}
    testid="preview-mode"
    onchange={(v) => ctl.setPreviewMode(v)}
  />
  <p class="hint">
    Like a vision mixer: this window shows what you’re preparing, while the output windows (and a
    small monitor in the corner) stay on what’s live. Take it live with <b>Cut</b>, a timed
    <b>Fade</b> (<kbd>Space</kbd>) or the <b>T-bar</b>. Auto show pauses meanwhile.
  </p>
</section>

<section class="sec" data-tour="record">
  <h2>Record</h2>
  {#if ctl.canRecord}
    <p class="hint first">
      Records the main picture and the music to a video file on this device (<kbd>⇧R</kbd>).
    </p>
    <div class="row">
      <button
        class:primary={ctl.recordingSince === null}
        class:rec={ctl.recordingSince !== null}
        data-testid="record"
        disabled={ctl.savingRecording}
        onclick={() => ctl.toggleRecording()}
        >{ctl.savingRecording
          ? 'Saving…'
          : ctl.recordingSince === null
            ? 'Start recording'
            : 'Stop and save'}</button
      >
      {#if ctl.recordingSince !== null}<span class="chip warn" data-testid="recording"
          ><Icon name="record" /> REC</span
        >{/if}
    </div>
  {:else}
    <p class="hint first">This browser can’t record video.</p>
  {/if}
</section>

{#if wm}
  <section class="sec">
    <div class="sec-head">
      <h2>Screens</h2>
      <button class="link" onclick={() => ctl.detectScreens()}
        >{screens.length ? 'Refresh' : 'Detect screens'}</button
      >
    </div>
    {#if screens.length}
      <div class="screens">
        {#each screens as s (s.id)}
          <div class="screen">
            <span class="small">{s.label}</span>
            <span class="chip">{s.width}×{s.height}</span>
            <button onclick={() => ctl.openOutput(s.id)}>Open here</button>
          </div>
        {/each}
      </div>
    {:else}
      <p class="hint">
        Chrome and Edge can place output windows on each screen automatically (asks for permission).
      </p>
    {/if}
  </section>
{/if}

<section class="sec">
  <h2>Layout</h2>
  <div class="row">
    <button
      class:active={mode === 'mirror'}
      onclick={() => ctl.setOutputMode('mirror')}
      data-testid="mode-mirror">Mirror</button
    >
    <button
      class:active={mode === 'tile'}
      onclick={() => ctl.setOutputMode('tile')}
      data-testid="mode-tile">Tile (video wall)</button
    >
  </div>
  {#if mode === 'tile'}
    <label class="field">
      <span>Bezel gap</span>
      <input
        type="number"
        min="0"
        max="400"
        value={ctl.outputs.bezelPx}
        onchange={(e) => ctl.outputs.setBezel(Number(e.currentTarget.value))}
      />
    </label>
    {#if !outs.every((o) => o.screenId)}
      <label class="field">
        <span>Grid</span>
        <div class="row">
          <input
            type="number"
            min="1"
            max="4"
            value={ctl.outputs.gridRows}
            aria-label="Rows"
            onchange={(e) =>
              ctl.outputs.setGrid(Number(e.currentTarget.value), ctl.outputs.gridCols)}
          />
          ×
          <input
            type="number"
            min="1"
            max="6"
            value={ctl.outputs.gridCols}
            aria-label="Columns"
            onchange={(e) =>
              ctl.outputs.setGrid(ctl.outputs.gridRows, Number(e.currentTarget.value))}
          />
        </div>
      </label>
    {/if}
    <p class="hint">
      Tiling renders one large image across all outputs. With detected screens the layout follows
      their real positions; otherwise outputs fill the grid in order. The bezel gap hides imagery
      “behind” screen frames.
    </p>
  {/if}
</section>

<section class="sec">
  <h2>Outputs ({outs.length})</h2>
  {#if outs.length === 0}
    <p class="hint">None open.</p>
  {/if}
  <div class="list">
    {#each outs as o (o.id)}
      <div class="item out" data-testid="output-item">
        <span class="badge">{o.label}</span>
        <div class="grow">
          <div class="small">{o.width}×{o.height}{o.fullscreen ? ' · fullscreen' : ''}</div>
          <div class="row small">
            <select
              value={String(o.config.rotation)}
              aria-label="Rotation"
              onchange={(e) =>
                ctl.outputs.configure(o.id, {
                  rotation: Number(e.currentTarget.value) as 0 | 90 | 180 | 270,
                })}
            >
              <option value="0">0°</option><option value="90">90°</option><option value="180"
                >180°</option
              ><option value="270">270°</option>
            </select>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={o.config.brightness}
              aria-label="Brightness"
              title="Brightness"
              onchange={(e) =>
                ctl.outputs.configure(o.id, { brightness: Number(e.currentTarget.value) })}
            />
            <label class="row"
              ><input
                type="checkbox"
                checked={o.config.pattern === 'test'}
                onchange={(e) =>
                  ctl.outputs.configure(o.id, {
                    pattern: e.currentTarget.checked ? 'test' : 'none',
                  })}
              /> test</label
            >
          </div>
        </div>
        <button class="icon-btn" aria-label="Close output" onclick={() => ctl.outputs.close(o.id)}
          ><Icon name="close" /></button
        >
      </div>
    {/each}
  </div>
</section>

<style>
  button.rec {
    border-color: var(--danger);
    color: var(--danger);
  }
  .first {
    margin: 0 0 10px;
  }
  .screens {
    display: grid;
    gap: 6px;
  }
  .screen {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
  }
  .out select {
    font-size: 11.5px;
    padding: 2px 4px;
  }
  .out input[type='range'] {
    width: 80px;
    accent-color: var(--accent);
  }
  .badge {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--accent) 25%, transparent);
    font-weight: 600;
  }
  input[type='number'] {
    width: 64px;
  }
</style>
